<template>
  <PageTemplate>
    <h1>{{ getViewTitle() }}</h1>
    <div
      v-if="!isEditReplyMode"
      class="row"
    >
      <div
        v-if="thread.title"
        class="col-lg-9"
      >
        <v-card :title="thread.title">
          <v-comment
            :title="getPostedBy(thread.user)"
            :date="getLocalDate(thread.created_at)"
            :is-edit-action="isEditAccess(thread.user.id)"
            :is-delete-action="isDeleteAccess(thread.user.id)"
            @editItem="editThread($event, thread.id)"
            @deleteItem="deleteThread($event, thread.id)"
          >
            <div
              class="mb-2"
              v-html="thread.post"
            />
            <!--Listing thread attachments -->
            <fieldset
              v-if="thread.documents && thread.documents.length > 0"
            >
              <v-label text="Attachments" />
              <v-document
                v-for="(doc, index) in thread.documents"
                :key="index"
                :title="doc.name"
                is-small
                @click="downloadDocument(doc.id)"
              />
            </fieldset>
          </v-comment>
          <div v-if="showCreateReply">
            <fieldset class="fieldset">
              <v-wysiwyg
                v-model="postContent"
                class="wysiwyg"
                placeholder="Enter content"
                @trix-file-accept="fileAccept"
                @trix-attachment-add="attachmentAdd"
                @trix-attachment-remove="attachmentRemove"
              />
            </fieldset>
            <fieldset class="fieldset">
              <v-file-upload
                v-for="(file, index) in files"
                :key="index"
                :file-name="file.name"
                is-small
                @clear="attachmentRemove(file)"
              />
            </fieldset>
            <v-button-group>
              <v-button
                has-min-width
                :is-disabled="enableDisablePost()"
                @click="post"
              >
                Post
              </v-button>

              <v-button
                color="secondary"
                has-min-width
                @click="performCancel"
              >
                Cancel
              </v-button>
            </v-button-group>
          </div>
          <v-comment
            v-for="reply in thread.replies"
            :key="reply.id"
            :title="getPostedBy(reply.user)"
            :date="getLocalDate(reply.created_at)"
            :is-edit-action="isEditAccess(reply.user.id)"
            :is-delete-action="isDeleteAccess(reply.user.id)"
            @editItem="editReply($event, reply.id)"
            @deleteItem="deleteReply($event, reply.id)"
          >
            <div
              class="mb-2"
              v-html="reply.post"
            />
            <!--Listing thread attachements -->
            <fieldset
              v-if="reply.documents && reply.documents.length > 0"
            >
              <v-label text="Attachments" />
              <v-document
                v-for="(doc, index) in reply.documents"
                :key="index"
                :title="doc.name"
                is-small
                @click="downloadDocument(doc.id)"
              />
            </fieldset>
          </v-comment>
        </v-card>
      </div>
      <div class="col-lg-3">
        <v-button
          size="lg"
          is-block
          @click="createReply"
        >
          Reply
        </v-button>
        <v-button
          size="lg"
          is-block
          :color="following ? 'danger' : 'secondary'"
          @click="followUnFollowAction()"
        >
          {{ following ? 'Unfollow' : "Follow" }}
        </v-button>
        <v-card
          v-if="getThreadDocuments().length !== 0"
          title="Related Documents"
        >
          <v-document
            v-for="doc in getThreadDocuments()"
            :key="doc.id"
            :title="doc.name"
            @click="downloadDocument(doc.id)"
          />
        </v-card>
      </div>
    </div>
    <div
      v-if="isEditReplyMode"
      class="row"
    >
      <div class="col-lg-9">
        <v-card :title="getViewTitle()">
          <fieldset class="fieldset">
            <v-label text="Reply" />
            <v-wysiwyg
              v-model="postContent_EditReply"
              class="wysiwyg"
              placeholder="Enter content"
              @trix-file-accept="fileAcceptEditReply"
              @trix-attachment-add="attachmentAddEditReply"
              @trix-attachment-remove="attachmentRemoveEditReply"
            />
          </fieldset>
          <fieldset class="fieldset">
            <v-label text="Attachments" />
            <v-file-upload
              v-for="(file, index) in files_EditReply"
              :key="index"
              :file-name="file.name"
              is-small
              @clear="attachmentRemoveEditReply(file)"
            />
          </fieldset>
        </v-card>
        <v-button-group>
          <v-button
            size="lg"
            has-min-width
            :is-disabled="enableDisablePost_editReply()"
            @click="postEditReply"
          >
            Post
          </v-button>

          <v-button
            size="lg"
            color="secondary"
            has-min-width
            @click="performCancelEditReply"
          >
            Cancel
          </v-button>
        </v-button-group>
      </div>
    </div>
    <v-modal
      v-if="deleteModal"
      @close="closeModal"
    >
      <template #header>
        {{ modalConfirmed ? "Success" : "Confirmation" }}
      </template>
      <p
        v-if="modalConfirmed"
        v-html="this.deleteModal.actionMsg"
      />
      <p
        v-else
        v-html="this.deleteModal.confirmMsg"
      />
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="!modalConfirmed"
            color="danger"
            size="lg"
            has-min-width
            @click="performDelete(deleteModal)"
          >
            Yes, delete
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            {{ modalConfirmed ? "OK, got it!" : "Cancel" }}
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
  </PageTemplate>
</template>
<script>
  import PageTemplate from "@/templates/Page/index.vue";
  import Button from "@/atoms/Button";
  import Label from "@/atoms/Label";
  import ButtonGroup from "@/molecules/ButtonGroup";
  import FileUpload from "@/molecules/FileUpload";
  import Document from "@/molecules/Document";
  import Comment from "@/molecules/Comment";
  import Card from "@/molecules/Card";
  import VueTrix from "vue-trix";
  import { buildBreadCrumb } from "@/common/tableFunctions";
  import { formatDate, addBlankTargets } from "@/common/formatting";
  import routes from "@/common/routes";
  import Modal from "@/atoms/Modal";
  import {
    GET_THREAD_DETAILS,
    CREATE_FORUM_REPLY,
    GET_FORUM_DOCUMENT,
    DELETE_FORUM_THREAD,
    EDIT_FORUM_REPLY,
    DELETE_FORUM_REPLY,
    GET_FOLLOWING_STATUS,
    FOLLOW_UNFOLLOW
  } from "@/store/actions.type";

  export default {
    name: "Discussion",
    components: {
      "v-document": Document,
      "v-comment": Comment,
      "v-button": Button,
      "v-button-group": ButtonGroup,
      "v-card": Card,
      "v-wysiwyg": VueTrix,
      "v-file-upload": FileUpload,
      "v-label": Label,
      "v-modal": Modal,
      PageTemplate,
    },
    data: function () {
      return {
        thread: {
          title: " ",
          id: "",
          user: {
            id: 1
          }
        },
        following: false,
        postContent: '',
        showCreateReply: false,
        files: [],
        isEditReplyMode: false,
        selectedReply: {},
        postContent_EditReply: '',
        files_EditReply: [],
        isPosting: false,
        deleteModal: null,
        modalConfirmed: false,
      };
    },
    watch: {},
    mounted() {
      this.getSelectedThread();
      this.getFollowingStatus();
    },
    methods: {
      enableDisablePost() {
        if(this.isPosting)
          return this.isPosting;
        return this.postContent === '';
      },
      enableDisablePost_editReply() {
        if(this.isPosting)
          return this.isPosting;
        return this.postContent_EditReply === '';
      },
      updateBreadCrumb: function (fn) {
        let breadcrumbs = [];
        breadcrumbs['Dashboard'] = routes.DASHBOARD;
        breadcrumbs['Forum'] = routes.FORUM;
        breadcrumbs['' + fn] = '';
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      getViewTitle() {
        return this.isEditReplyMode ? 'Edit Reply' : 'Forum Discussion';
      },
      editThread($event, id) {
        this.$router.push({name: 'edit-discussion', params: {id: this.$route.params.id}})
      },
      performDelete() {
        if(this.deleteModal.deleteType === 'TOPIC') {
          this.deleteThreadAction(this.deleteModal.itemId);
        }else if(this.deleteModal.deleteType === 'REPLY') {
          this.performDeleteReplyAction(this.deleteModal.itemId);
        }
        this.closeModal();
      },
      deleteThread($event, id) {
        this.deleteModal = {};
        this.deleteModal.confirmMsg = 'Are you sure want to delete this forum Thread?';
        this.deleteModal.itemId = id;
        this.deleteModal.deleteType = 'TOPIC';
      },
      editReply($event, id) {
        this.isEditReplyMode = true;
        this.selectedReply = this.thread.replies.filter(ele => ele.id === id)[0];
        this.postContent_EditReply = this.selectedReply.post;
        this.files_EditReply = this.selectedReply.documents;
      },
      deleteReply($event, id) {
        this.deleteModal = {};
        this.deleteModal.confirmMsg = 'Are you sure want to delete this reply?';
        this.deleteModal.itemId = id;
        this.deleteModal.deleteType = 'REPLY'
      },
      isEditAccess(userId) {
        return userId === this.$store.state.auth.user.id;
      },
      isDeleteAccess(userId) {
        return userId === this.$store.state.auth.user.id;
      },
      getSelectedThread: function () {
        return this.$store
          .dispatch(GET_THREAD_DETAILS, {id: this.$route.params.id})
          .then(response => {
            this.thread = response;
            this.thread.replies.reverse();
            this.updateBreadCrumb(this.thread.title);
          })
          .catch(err => {
            console.error("Error: While loading thread details. " + err);
          });
      },
      getPostedBy(user) {
        if (!user)
          return "";
        return (
          (user.first_name ? user.first_name : "") +
          " " +
          (user.middle_name ? user.middle_name : "") +
          " " +
          (user.last_name ? user.last_name : "")
        );
      },
      getThreadDocuments() {
        let res = [];
        if (!this.thread.documents)
          return [];
        res = [...this.thread.documents];
        this.thread.replies.forEach(ele => {
          res = [...res, ...ele.documents];
        });
        return res;
      },
      fileAccept(e) {
        //check if this file is already attached.
        if (this.files.filter(ele => ele.name === e.file.name).length > 0) {
          this.showDuplicateFileAttachedModal(e.file.name);
          e.preventDefault();
          return false;
        }
      },
      showDuplicateFileAttachedModal(fileName) {
        this.modalConfirmed = true;
          this.deleteModal = {};
          this.deleteModal.headerText = 'Warning';
          this.deleteModal.actionMsg = 'File <strong>'+fileName+'</strong> is already attached.';
      },
      attachmentAdd(evt) {
        let file = evt.attachment.file;
        this.files.push(file);
      },
      attachmentRemove(evt) {
        let fileName = evt.attachment ? evt.attachment.file.name : evt.name;
        this.files = this.files.filter(ele => ele.name !== fileName);
      },
      post() {
        this.isPosting = true;
        var formData = new FormData();
        // adding attachement files
        if (this.files) {
          this.files.forEach((file, index) => {
            formData.append('files[]', file);
          });

        }
        formData.append('committeId', this.$store.getters.getSelectedCommittee);
        formData.append('filesCount', this.files.length);
        formData.append('postContent', addBlankTargets(this.postContent));
        formData.append('threadId', this.thread.id);
        let res = this.$store
          .dispatch(CREATE_FORUM_REPLY, formData)
          .then(response => {
            this.$store.state.forums.forums = [];
            // Navigate to Forum thread listing page.
            this.getSelectedThread();
            this.showCreateReply = false;
            this.isPosting = false;
            this.$notify({
              title: 'Reply to this thread added',
              text: 'Thread reply created successfully.',
              type: 'success'
            });
          })
          .catch(err => {
            this.isPosting = false;
            this.$notify({
              title: 'Error:',
              text: err,
              type: 'error'
            });
          });
      },
      performCancel(evt) {
        this.showCreateReply = false;
      },
      createReply() {
        this.showCreateReply = !this.showCreateReply;
        this.postContent = '';
        this.files = [];
      },
      downloadDocument(evt) {
        this.$store.state.auth.isLoading = true;
        this.$store
          .dispatch(GET_FORUM_DOCUMENT, {id: evt})
          .then(response => {
            return response;
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });

      },
      deleteThreadAction(id) {
        let res = this.$store
          .dispatch(DELETE_FORUM_THREAD, {id: id})
          .then(response => {
            this.$store.state.forums.forums = [];
            // Navigate to Forum thread listing page.
            this.$router.push({name: 'forum'});
            this.$notify({
              title: 'Discussion Thread Delete',
              text: 'Discussion Thread Deleted Successfully.',
              type: 'success'
            });
          })
          .catch(err => {
            this.$notify({
              title: 'Error:',
              text: err,
              type: 'error'
            });
          });
      },
      postEditReply() {
        this.isPosting = true;
        var formData = new FormData();
        // adding attachement files
        if (this.files_EditReply) {
          this.files_EditReply.forEach((file, index) => {
            if(file.uploadable_id) {
              formData.append('existingFiles[]', file.name);
            }else {
              formData.append ('files[]', file);
            }
          });

        }
          formData.append('committeId', this.$store.getters.getSelectedCommittee);
          formData.append('filesCount', this.files_EditReply.length);
          formData.append('postContent', addBlankTargets(this.postContent_EditReply));
          formData.append('replyId', this.selectedReply.id);
          formData.append('_method', 'put');
          let res = this.$store
        .dispatch(EDIT_FORUM_REPLY, {formData: formData, id: this.selectedReply.id})
        .then(response => {
          // Navigate to Forum thread listing page.
          this.getSelectedThread();
          this.showCreateReply = false;
          this.isEditReplyMode = false;
          this.isPosting = false;
          this.$notify({
              title: 'Reply to this thread Edited',
              text: 'Thread reply Edited successfully.',
              type: 'success'
            });
          })
          .catch(err => {
            this.isPosting = false;
            this.$notify({
              title: 'Error:',
              text: err,
              type: 'error'
            });
          });
      },
      performDeleteReplyAction(id) {
        let res = this.$store
          .dispatch(DELETE_FORUM_REPLY, {id: id})
          .then(response => {
            this.getSelectedThread();
            this.$store.state.forums.forums = [];
            this.showCreateReply = false;
            this.isEditReplyMode = false;
            this.$notify({
              title: 'Reply to this thread Deleted',
              text: 'Thread reply deleted successfully.',
              type: 'success'
            });
          })
          .catch(err => {
            this.$notify({
              title: 'Error:',
              text: err,
              type: 'error'
            });
          });
      },
      performCancelEditReply(evt) {
        this.isEditReplyMode = false;
      },
      fileAcceptEditReply(e) {
        //check if this file is already attached.
        if (this.files_EditReply.filter(ele => ele.name === e.file.name).length > 0) {
          this.showDuplicateFileAttachedModal(e.file.name);
          e.preventDefault();
          return false;
        }
      },
      attachmentAddEditReply(evt) {
        let file = evt.attachment.file;
        this.files_EditReply.push(file);
      },
      attachmentRemoveEditReply(evt) {

        let fileName = evt.attachment ? evt.attachment.file.name : evt.name;
        this.files_EditReply = this.files_EditReply.filter(ele => ele.name !== fileName);
      },
      getFollowingStatus() {
        this.$store
          .dispatch(GET_FOLLOWING_STATUS, {id: this.$route.params.id})
          .then(response => {
            this.following = response;
          })
          .catch(err => {
            console.error("Error: While loading thread details. " + err);
          });
      },
      followUnFollowAction() {
        this.$store
          .dispatch(FOLLOW_UNFOLLOW, {id: this.$route.params.id, isFollowing: this.following})
          .then(response => {
            this.following = !this.following;
          })
          .catch(err => {
            console.error("Error: While loading thread details. " + err);
          });
      },
      getLocalDate(date) {
       return formatDate(date);
      },
      closeModal() {
        this.deleteModal = null;
        this.modalConfirmed = false;
      },
    }
  }
</script>
